import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useSpiritualStore } from '../hooks/useSpiritualStore';
import Fr00tLogo from '../components/Fr00tLogo';

// Icons
import {
  Calculator,
  Clock,
  Headphones,
  Sparkles,
  BookOpen,
  Edit3,
  Activity,
  Circle,
  User,
  LogIn,
  LogOut,
  X,
  Sparkles as SparklesIcon,
  Heart,
  Eye,
  TrendingUp
} from 'lucide-react';

// Components
import MeditationTimer from '../components/MeditationTimer';
import GuidedMeditations from '../components/GuidedMeditations';
import PersonalNumerologyChart from '../components/PersonalNumerologyChart';
import VisualHarmonyStudio from '../components/VisualHarmonyStudio';
import AudioHarmonyLab from '../components/AudioHarmonyLab';
import WisdomLibrary from '../components/WisdomLibrary';
import PersonalJournal from '../components/PersonalJournal';
import LivingWisdomOracle from '../components/LivingWisdomOracle';
import EvolutionTracker from '../components/EvolutionTracker';
import HabitTracker from '../components/HabitTracker';
import GrowthDashboard from '../components/GrowthDashboard';
import CymaticHologram3D from '../components/CymaticHologram3D';
import { useGlobalAudioAnalyser } from '../hooks/useGlobalAudioAnalyser';
import PersonalProfile from '../components/PersonalProfile';
import NumerologyCalculator from '../components/NumerologyCalculator';
import NumbersAreAStoryPanel from '../components/NumbersAreAStoryPanel';
import NumerologyColorLegend from '../components/NumerologyColorLegend';
import { calculateNameNumerology } from '../domains/numerology/calculator';
import { getArchetype, getCompatibleNumbers, getChallengingNumbers } from '../domains/numerology/archetypes';
import toast from 'react-hot-toast';

/**
 * Hardware Rack Unit Component
 * 
 * Represents a physical rack-mounted unit with metal casing, screw holes, and labels
 */
const RackUnit: React.FC<{
  minHeight?: number; // Minimum height in pixels
  unitHeight?: number; // Display height in rack units for visual reference
  label: string;
  unitNumber?: number;
  children: React.ReactNode;
  accentColor?: string;
}> = ({ minHeight = 400, unitHeight = 3, label, unitNumber, children, accentColor = '#8b5cf6' }) => {
  const visualHeight = unitHeight * 60; // Larger visual units for better scaling
  
  return (
    <div
      style={{
        position: 'relative',
        minHeight: `${minHeight}px`,
        marginBottom: '12px', // Rack spacing between units
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Rack Mount Rails - Left */}
      <div
        style={{
          position: 'absolute',
          left: '-24px',
          top: '0',
          bottom: '0',
          width: '20px',
          background: 'linear-gradient(to right, #1a1a1a 0%, #2d2d2d 100%)',
          borderLeft: '3px solid #0a0a0a',
          borderRight: '2px solid #404040',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '8px 0',
          gap: '40px'
        }}
      >
        {/* Screw holes */}
        {Array.from({ length: Math.max(3, Math.floor(minHeight / 120)) }).map((_, i) => (
          <div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, #4a4a4a 0%, #1a1a1a 70%)',
              border: '1px solid #0a0a0a',
              boxShadow: 'inset 0 0 2px rgba(0,0,0,0.8), 0 1px 1px rgba(255,255,255,0.1)'
            }}
          />
        ))}
      </div>

      {/* Rack Mount Rails - Right */}
      <div
        style={{
          position: 'absolute',
          right: '-24px',
          top: '0',
          bottom: '0',
          width: '20px',
          background: 'linear-gradient(to left, #1a1a1a 0%, #2d2d2d 100%)',
          borderRight: '3px solid #0a0a0a',
          borderLeft: '2px solid #404040',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '8px 0',
          gap: '40px'
        }}
      >
        {/* Screw holes */}
        {Array.from({ length: Math.max(3, Math.floor(minHeight / 120)) }).map((_, i) => (
          <div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, #4a4a4a 0%, #1a1a1a 70%)',
              border: '1px solid #0a0a0a',
              boxShadow: 'inset 0 0 2px rgba(0,0,0,0.8), 0 1px 1px rgba(255,255,255,0.1)'
            }}
          />
        ))}
      </div>

      {/* Metal Casing */}
      <div
        style={{
          flex: 1,
          background: `
            linear-gradient(135deg, 
              rgba(45, 45, 45, 0.3) 0%, 
              rgba(30, 30, 30, 0.25) 25%, 
              rgba(42, 42, 42, 0.3) 50%, 
              rgba(30, 30, 30, 0.25) 75%, 
              rgba(45, 45, 45, 0.3) 100%
            )
          `,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(10, 10, 10, 0.5)',
          borderRadius: '4px',
          boxShadow: `
            inset 0 1px 3px rgba(255,255,255,0.1),
            inset 0 -1px 3px rgba(0,0,0,0.3),
            0 4px 8px rgba(0,0,0,0.4),
            0 0 0 1px rgba(64, 64, 64, 0.5)
          `,
          position: 'relative',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Top Screw Row */}
        <div
          style={{
            height: '24px',
            borderBottom: '2px solid #0a0a0a',
            background: 'linear-gradient(to bottom, #1a1a1a 0%, #2d2d2d 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
            position: 'relative'
          }}
        >
          {/* Unit Label */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '11px',
              fontFamily: 'monospace',
              color: accentColor,
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: accentColor,
                boxShadow: `0 0 6px ${accentColor}`,
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />
            {label}
          </div>

          {/* Unit Number Badge */}
          {unitNumber !== undefined && (
            <div
              style={{
                width: '20px',
                height: '16px',
                background: '#0a0a0a',
                border: `1px solid ${accentColor}`,
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontFamily: 'monospace',
                color: accentColor,
                fontWeight: '700'
              }}
            >
              {unitNumber}
            </div>
          )}

          {/* Corner Screws */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {[0, 1].map(i => (
              <div
                key={i}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle at 30% 30%, #555 0%, #222 50%, #000 100%)',
                  border: '1px solid #0a0a0a',
                  boxShadow: `
                    inset 0 0 3px rgba(0,0,0,0.9),
                    inset -1px -1px 2px rgba(255,255,255,0.1),
                    0 1px 2px rgba(0,0,0,0.8)
                  `
                }}
              />
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div
          style={{
            flex: 1,
            padding: '16px',
            overflow: 'visible',
            background: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          {children}
        </div>

        {/* Bottom Border with Vents */}
        <div
          style={{
            height: '16px',
            borderTop: '2px solid #0a0a0a',
            background: `
              repeating-linear-gradient(
                90deg,
                #1a1a1a 0px,
                #1a1a1a 8px,
                #0a0a0a 8px,
                #0a0a0a 10px
              )
            `,
            opacity: 0.6
          }}
        />
      </div>
    </div>
  );
};

/**
 * Master Dashboard - Hardware Rack Interface
 * 
 * All tools consolidated into a unified rack-mounted hardware system.
 * Everything is permanently mounted in place - no collapsing panels.
 */
const MasterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { colors, gradients, theme } = useTheme();
  const { user, calculateNumerology, isAuthenticated, logout } = useSpiritualStore();
  
  const [quickCalcInput, setQuickCalcInput] = useState('');
  const [quickCalcResult, setQuickCalcResult] = useState<any>(null);
  const [showFullCalculator, setShowFullCalculator] = useState(false);
  const [meditationMode, setMeditationMode] = useState<'timer' | 'guided'>('guided');
  const [selectedCymaticPattern, setSelectedCymaticPattern] = useState<string>('flower-of-life');
  const [cymaticAudioData, setCymaticAudioData] = useState<{ frequencyData: Uint8Array; averageAmplitude: number } | null>(null);
  const [isCymaticPlaying, setIsCymaticPlaying] = useState(false);
  
  // Global audio analyser for 3D hologram
  const { audioData: globalAudioData, isPlaying: isGlobalAudioPlaying } = useGlobalAudioAnalyser();

  const handleQuickCalculate = () => {
    if (!quickCalcInput.trim()) {
      toast.error('Please enter text to calculate');
      return;
    }
    try {
      // Calculate using full archetype system
      const nameResult = calculateNameNumerology(quickCalcInput);
      const archetype = getArchetype(nameResult.expressionNumber);
      
      const fullResult = {
        text: quickCalcInput,
        calculation: nameResult,
        archetype,
        expressionNumber: nameResult.expressionNumber,
        soulUrgeNumber: nameResult.soulUrgeNumber,
        personalityNumber: nameResult.personalityNumber,
        expressionArchetype: getArchetype(nameResult.expressionNumber),
        soulUrgeArchetype: getArchetype(nameResult.soulUrgeNumber),
        personalityArchetype: getArchetype(nameResult.personalityNumber),
        compatibleNumbers: getCompatibleNumbers(nameResult.expressionNumber),
        challengingNumbers: getChallengingNumbers(nameResult.expressionNumber),
        letterBreakdown: nameResult.letterBreakdown
      };
      
      setQuickCalcResult(fullResult);
      setShowFullCalculator(true);
      toast.success('Numerology calculated with full archetypal depth!');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Calculation failed');
    }
  };

  return (
    <div
      className="fr00t-hardware-rack"
      style={{
        minHeight: '100vh',
        background: 'transparent',
        padding: '2rem',
        position: 'relative',
        overflowX: 'hidden',
        overflowY: 'auto',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box'
      }}
    >
      {/* Top Control Panel */}
      <div style={{ marginBottom: '2rem', position: 'relative', zIndex: 10 }}>
        <RackUnit minHeight={120} unitHeight={2} label="FR00T SYSTEM" unitNumber={0} accentColor={colors.purple}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
            {/* System Logo/Title */}
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '4px'
              }}>
                <Fr00tLogo size={32} />
                <span style={{
                  fontSize: '1.5rem',
                  fontFamily: 'monospace',
                  fontWeight: '700',
                  background: gradients.rainbow,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '2px'
                }}>
                  FR00T CONTROL UNIT
                </span>
              </div>
              <div style={{
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                color: colors.gray,
                letterSpacing: '1px'
              }}>
                F(6) + R(9) + 0(0) + 0(0) + T(2) = 17 → 8
              </div>
            </div>

            {/* Quick Calculator */}
            <div style={{ flex: 2, margin: '0 1rem' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Calculator size={18} color={colors.purple} />
                <input
                  type="text"
                  value={quickCalcInput}
                  onChange={(e) => setQuickCalcInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleQuickCalculate()}
                  placeholder="Enter text, name, date, or concept..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: '2px solid #0a0a0a',
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040
                    `,
                    color: colors.light,
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    outline: 'none'
                  }}
                />
                <motion.button
                  onClick={handleQuickCalculate}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '8px 16px',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${colors.purple}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040,
                      0 0 8px ${colors.purple}40
                    `,
                    color: colors.purple,
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    outline: 'none'
                  }}
                >
                  CALC
                </motion.button>
              </div>
              {quickCalcResult && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${colors.purple}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040,
                      0 0 6px ${colors.purple}30
                    `,
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowFullCalculator(true)}
                  whileHover={{ scale: 1.02 }}
                >
                  <div style={{
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    color: colors.purple,
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    {quickCalcResult.expressionNumber} {quickCalcResult.expressionArchetype?.isMaster && '(MASTER)'} - {quickCalcResult.expressionArchetype?.title || 'Calculated'}
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    fontFamily: 'monospace',
                    color: colors.gray
                  }}>
                    Click for full archetypal analysis →
                  </div>
                </motion.div>
              )}
            </div>

            {/* Auth Controls */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {isAuthenticated && user ? (
                <>
                  <div                   style={{
                    padding: '8px 12px',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `1px solid ${colors.purple}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 2px rgba(255,255,255,0.1),
                      inset 0 -1px 2px rgba(0,0,0,0.5),
                      0 1px 2px rgba(0,0,0,0.6),
                      0 0 0 1px #404040
                    `,
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    color: colors.purple,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <User size={14} />
                    {user.username}
                  </div>
                  <motion.button
                    onClick={() => { logout(); toast.success('Logged out'); }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      padding: '8px 12px',
                      background: `
                        linear-gradient(135deg, 
                          #2d2d2d 0%, 
                          #1e1e1e 25%, 
                          #2a2a2a 50%, 
                          #1e1e1e 75%, 
                          #2d2d2d 100%
                        )
                      `,
                      border: `1px solid ${colors.red}`,
                      borderRadius: '4px',
                      boxShadow: `
                        inset 0 1px 2px rgba(255,255,255,0.1),
                        inset 0 -1px 2px rgba(0,0,0,0.5),
                        0 1px 2px rgba(0,0,0,0.6),
                        0 0 0 1px #404040
                      `,
                      color: colors.red,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      outline: 'none'
                    }}
                  >
                    <LogOut size={14} />
                    LOGOUT
                  </motion.button>
                </>
              ) : (
                <motion.button
                  onClick={() => navigate('/auth')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '8px 16px',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${colors.purple}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040,
                      0 0 8px ${colors.purple}40
                    `,
                    color: colors.purple,
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    outline: 'none'
                  }}
                >
                  <LogIn size={14} />
                  SIGN IN
                </motion.button>
              )}
            </div>
          </div>
        </RackUnit>
      </div>

      {/* Main Rack System */}
      <div
        style={{
          display: 'grid',
          // Custom column widths: Left (wider), Middle (normal), Right (half width)
          // Responsive: stacks on smaller screens, custom proportions on larger screens
          gridTemplateColumns: 'minmax(0, 2.5fr) minmax(0, 1.5fr) minmax(0, 1fr)',
          gap: '1.5rem',
          position: 'relative',
          paddingLeft: '16px',
          paddingRight: '16px',
          alignItems: 'start',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        {/* Left Column - CYMATIC FEATURES HIGHLIGHTED */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', maxWidth: '100%' }}>
          {/* 🌟 CYMATIC HOLOGRAM 3D - TOP FEATURE #1 🌟 */}
          <RackUnit minHeight={650} unitHeight={6} label="⚡ CYMATIC HOLOGRAM 3D ⚡" unitNumber={1} accentColor="#00d9ff">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem', width: '100%', overflow: 'hidden' }}>
              <CymaticHologram3D 
                patternType={selectedCymaticPattern}
                audioData={globalAudioData || cymaticAudioData}
                isPlaying={isGlobalAudioPlaying || isCymaticPlaying}
                width={Math.min(550, typeof window !== 'undefined' ? window.innerWidth * 0.4 : 550)}
                height={Math.min(550, typeof window !== 'undefined' ? window.innerWidth * 0.4 : 550)}
              />
            </div>
          </RackUnit>

          {/* Meditation Unit */}
          <RackUnit minHeight={500} unitHeight={4} label="MEDITATION & MINDFULNESS" unitNumber={2} accentColor={colors.purple}>
            <div style={{ marginBottom: '1rem', width: '100%', maxWidth: '100%' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
                <motion.button
                  onClick={() => setMeditationMode('timer')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${meditationMode === 'timer' ? colors.purple : '#0a0a0a'}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040
                      ${meditationMode === 'timer' ? `, 0 0 6px ${colors.purple}40` : ''}
                    `,
                    color: meditationMode === 'timer' ? colors.purple : colors.gray,
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    outline: 'none'
                  }}
                >
                  <Clock size={14} />
                  TIMER
                </motion.button>
                <motion.button
                  onClick={() => setMeditationMode('guided')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${meditationMode === 'guided' ? colors.purple : '#0a0a0a'}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040
                      ${meditationMode === 'guided' ? `, 0 0 6px ${colors.purple}40` : ''}
                    `,
                    color: meditationMode === 'guided' ? colors.purple : colors.gray,
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    outline: 'none'
                  }}
                >
                  <Headphones size={14} />
                  GUIDED
                </motion.button>
              </div>
              {meditationMode === 'timer' ? (
                <MeditationTimer onComplete={() => toast.success('Meditation complete!')} />
              ) : (
                <GuidedMeditations />
              )}
            </div>
          </RackUnit>

          {/* Wisdom Unit */}
          <RackUnit minHeight={900} unitHeight={7} label="WISDOM & KNOWLEDGE" unitNumber={3} accentColor={colors.indigo}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <div>
                <WisdomLibrary />
              </div>
              <div>
                <PersonalJournal />
              </div>
              <div>
                <LivingWisdomOracle />
              </div>
            </div>
          </RackUnit>
        </div>

        {/* Middle Column */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', maxWidth: '100%' }}>
          {/* 🌟 CYMATIC GENERATOR - TOP FEATURE #2 🌟 */}
          <RackUnit minHeight={700} unitHeight={6} label="⚡ CYMATIC SOUND GENERATOR ⚡" unitNumber={4} accentColor="#a855f7">
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <VisualHarmonyStudio 
                onPatternChange={setSelectedCymaticPattern}
                onAudioDataChange={(audioData, isPlaying) => {
                  setCymaticAudioData(audioData);
                  setIsCymaticPlaying(isPlaying);
                }}
              />
            </div>
          </RackUnit>

          {/* Global Numerology Legend */}
          <RackUnit minHeight={280} unitHeight={2} label="GLOBAL NUMEROLOGY LEGEND" unitNumber={5} accentColor={colors.purple}>
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <NumerologyColorLegend title="Fr00t Global Color Legend" />
            </div>
          </RackUnit>

          {/* Numerology Unit */}
          <RackUnit minHeight={500} unitHeight={4} label="NUMEROLOGY CALCULATOR" unitNumber={6} accentColor={colors.indigo}>
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <NumerologyCalculator />
            </div>
          </RackUnit>

          {/* Personal Profile – centered column, bottom position */}
          <RackUnit minHeight={600} unitHeight={5} label="PERSONAL PROFILE" unitNumber={7} accentColor={colors.emerald}>
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <PersonalProfile />
            </div>
          </RackUnit>
        </div>
        
        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', maxWidth: '100%' }}>
          {/* Numbers Are A Story - Real-Time Numerology */}
          <RackUnit minHeight={900} unitHeight={8} label="NUMBERS ARE A STORY" unitNumber={7} accentColor={colors.purple}>
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <NumbersAreAStoryPanel />
            </div>
          </RackUnit>

          {/* Practice & Growth */}
          <RackUnit minHeight={700} unitHeight={6} label="PRACTICE & GROWTH" unitNumber={8} accentColor={colors.amber}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <EvolutionTracker />
              <HabitTracker />
              <GrowthDashboard />
            </div>
          </RackUnit>

          {/* Audio Harmony Lab */}
          <RackUnit minHeight={600} unitHeight={5} label="AUDIO HARMONY LAB" unitNumber={9} accentColor={colors.teal}>
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
              <AudioHarmonyLab />
            </div>
          </RackUnit>
        </div>
      </div>

      {/* Rack Bottom */}
      <div style={{
        marginTop: '2rem',
        height: '32px',
        background: 'linear-gradient(to bottom, #2d2d2d 0%, #1a1a1a 100%)',
        border: '2px solid #0a0a0a',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: colors.gray,
        letterSpacing: '2px'
      }}>
        RACK MOUNT SYSTEM — ALL UNITS OPERATIONAL
      </div>

      {/* Full Calculator Results Modal */}
      <AnimatePresence>
        {showFullCalculator && quickCalcResult && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFullCalculator(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0, 0, 0, 0.9)',
                backdropFilter: 'blur(10px)',
                zIndex: 9998,
                overflow: 'hidden'
              }}
            />

            {/* Modal Container - Ensures proper centering and bounds */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                pointerEvents: 'none',
                padding: '2rem',
                boxSizing: 'border-box'
              }}
            >
              {/* Modal - Properly Centered with Viewport Constraints */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 50 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 'clamp(320px, 90vw, 1000px)',
                  maxWidth: '1000px',
                  height: 'clamp(400px, 90vh, 800px)',
                  maxHeight: '90vh',
                  background: `
                    linear-gradient(135deg, 
                      #2d2d2d 0%, 
                      #1e1e1e 25%, 
                      #2a2a2a 50%, 
                      #1e1e1e 75%, 
                      #2d2d2d 100%
                    )
                  `,
                  border: '3px solid #0a0a0a',
                  borderRadius: '8px',
                  boxShadow: `
                    inset 0 1px 3px rgba(255,255,255,0.1),
                    inset 0 -1px 3px rgba(0,0,0,0.5),
                    0 20px 60px rgba(0,0,0,0.9),
                    0 0 0 1px #404040
                  `,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '0',
                  boxSizing: 'border-box',
                  pointerEvents: 'auto'
                }}
              >
              {/* Modal Header */}
              <div style={{
                padding: '1rem 1.5rem',
                borderBottom: '2px solid #0a0a0a',
                background: 'linear-gradient(to bottom, #1a1a1a 0%, #2d2d2d 100%)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{
                    fontSize: '1.25rem',
                    fontFamily: 'monospace',
                    fontWeight: '700',
                    color: colors.purple,
                    marginBottom: '4px'
                  }}>
                    ARCHETYPAL ANALYSIS: "{quickCalcResult.text.toUpperCase()}"
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    color: colors.gray
                  }}>
                    Universal numerological meanings for any text, date, or concept
                  </div>
                </div>
                <motion.button
                  onClick={() => setShowFullCalculator(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: `
                      radial-gradient(circle at 30% 30%, #555 0%, #222 50%, #000 100%)
                    `,
                    border: `2px solid ${colors.red}`,
                    boxShadow: `
                      inset 0 0 3px rgba(0,0,0,0.9),
                      inset -1px -1px 2px rgba(255,255,255,0.1),
                      0 1px 2px rgba(0,0,0,0.8),
                      0 0 6px ${colors.red}40
                    `,
                    color: colors.red,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none'
                  }}
                >
                  <X size={18} />
                </motion.button>
              </div>

              {/* Modal Content */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '1.5rem',
                background: `
                  linear-gradient(135deg, 
                    rgba(15, 23, 42, 0.8) 0%, 
                    rgba(30, 41, 59, 0.6) 50%,
                    rgba(15, 23, 42, 0.8) 100%
                  )
                `
              }}>
                {/* Expression Number - Main */}
                <div style={{
                  background: `
                    linear-gradient(135deg, 
                      #2d2d2d 0%, 
                      #1e1e1e 25%, 
                      #2a2a2a 50%, 
                      #1e1e1e 75%, 
                      #2d2d2d 100%
                    )
                  `,
                  border: `2px solid ${colors.purple}`,
                  borderRadius: '4px',
                  boxShadow: `
                    inset 0 1px 3px rgba(255,255,255,0.1),
                    inset 0 -1px 3px rgba(0,0,0,0.5),
                    0 2px 4px rgba(0,0,0,0.6),
                    0 0 0 1px #404040,
                    0 0 8px ${colors.purple}30
                  `,
                  padding: '1.5rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <SparklesIcon size={20} color={colors.purple} />
                    <div>
                      <div style={{
                        color: colors.purple,
                        fontSize: '1rem',
                        fontFamily: 'monospace',
                        fontWeight: '700'
                      }}>
                        EXPRESSION NUMBER: {quickCalcResult.expressionNumber}
                        {quickCalcResult.expressionArchetype?.isMaster && ' (MASTER NUMBER)'}
                      </div>
                      <div style={{
                        color: colors.gray,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace'
                      }}>
                        Core expression and meaning
                      </div>
                    </div>
                  </div>

                  <div style={{
                    color: colors.purple,
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    marginBottom: '0.5rem'
                  }}>
                    {quickCalcResult.expressionArchetype?.title}
                  </div>

                  <div style={{
                    color: colors.light,
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                    marginBottom: '1rem'
                  }}>
                    {quickCalcResult.expressionArchetype?.coreMeaning}
                  </div>

                  <div style={{
                    color: colors.gray,
                    fontSize: '0.8rem',
                    lineHeight: '1.5',
                    fontStyle: 'italic',
                    marginBottom: '1rem'
                  }}>
                    {quickCalcResult.expressionArchetype?.spiritualMeaning}
                  </div>

                  {/* Strengths */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{
                      color: colors.emerald,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      STRENGTHS:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {quickCalcResult.expressionArchetype?.strengths.slice(0, 8).map((s: string, i: number) => (
                        <span
                          key={i}
                          style={{
                            padding: '0.375rem 0.75rem',
                            background: `
                              linear-gradient(135deg, 
                                #2d2d2d 0%, 
                                #1e1e1e 50%, 
                                #2d2d2d 100%
                              )
                            `,
                            border: `1px solid ${colors.emerald}`,
                            borderRadius: '4px',
                            boxShadow: `
                              inset 0 1px 2px rgba(255,255,255,0.1),
                              inset 0 -1px 2px rgba(0,0,0,0.5),
                              0 1px 2px rgba(0,0,0,0.6),
                              0 0 3px ${colors.emerald}30
                            `,
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            color: colors.emerald
                          }}
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Challenges */}
                  <div>
                    <div style={{
                      color: colors.amber,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      CHALLENGES:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {quickCalcResult.expressionArchetype?.challenges.slice(0, 6).map((c: string, i: number) => (
                        <span
                          key={i}
                          style={{
                            padding: '0.375rem 0.75rem',
                            background: `
                              linear-gradient(135deg, 
                                #2d2d2d 0%, 
                                #1e1e1e 50%, 
                                #2d2d2d 100%
                              )
                            `,
                            border: `1px solid ${colors.amber}`,
                            borderRadius: '4px',
                            boxShadow: `
                              inset 0 1px 2px rgba(255,255,255,0.1),
                              inset 0 -1px 2px rgba(0,0,0,0.5),
                              0 1px 2px rgba(0,0,0,0.6),
                              0 0 3px ${colors.amber}30
                            `,
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            color: colors.amber
                          }}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Soul Urge & Personality */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  {/* Soul Urge */}
                  <div style={{
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${colors.indigo}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040,
                      0 0 6px ${colors.indigo}30
                    `,
                    padding: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <Heart size={16} color={colors.indigo} />
                      <div style={{
                        color: colors.indigo,
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        fontWeight: '600'
                      }}>
                        VOWEL EXPRESSION: {quickCalcResult.soulUrgeNumber}
                      </div>
                    </div>
                    <div style={{
                      color: colors.light,
                      fontSize: '1rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      {quickCalcResult.soulUrgeArchetype?.title}
                    </div>
                    <div style={{
                      color: colors.gray,
                      fontSize: '0.75rem',
                      lineHeight: '1.5'
                    }}>
                      {quickCalcResult.soulUrgeArchetype?.essence}
                    </div>
                  </div>

                  {/* Personality */}
                  <div style={{
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${colors.emerald}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040,
                      0 0 6px ${colors.emerald}30
                    `,
                    padding: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <Eye size={16} color={colors.emerald} />
                      <div style={{
                        color: colors.emerald,
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        fontWeight: '600'
                      }}>
                        CONSONANT EXPRESSION: {quickCalcResult.personalityNumber}
                      </div>
                    </div>
                    <div style={{
                      color: colors.light,
                      fontSize: '1rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      {quickCalcResult.personalityArchetype?.title}
                    </div>
                    <div style={{
                      color: colors.gray,
                      fontSize: '0.75rem',
                      lineHeight: '1.5'
                    }}>
                      {quickCalcResult.personalityArchetype?.essence}
                    </div>
                  </div>
                </div>

                {/* Life Purpose & Connections */}
                <div style={{
                  background: `
                    linear-gradient(135deg, 
                      #2d2d2d 0%, 
                      #1e1e1e 25%, 
                      #2a2a2a 50%, 
                      #1e1e1e 75%, 
                      #2d2d2d 100%
                    )
                  `,
                  border: `2px solid ${colors.purple}`,
                  borderRadius: '4px',
                  boxShadow: `
                    inset 0 1px 3px rgba(255,255,255,0.1),
                    inset 0 -1px 3px rgba(0,0,0,0.5),
                    0 2px 4px rgba(0,0,0,0.6),
                    0 0 0 1px #404040,
                    0 0 6px ${colors.purple}20
                  `,
                  padding: '1.5rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    color: colors.purple,
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    marginBottom: '1rem'
                  }}>
                    CORE EXPRESSION & ARCHETYPAL CONNECTIONS
                  </div>

                  <div style={{
                    color: colors.light,
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                    marginBottom: '1rem'
                  }}>
                    <strong style={{ color: colors.purple }}>Core Expression:</strong> {quickCalcResult.expressionArchetype?.lifePurpose}
                  </div>

                  {/* Compatible Numbers */}
                  {quickCalcResult.compatibleNumbers && quickCalcResult.compatibleNumbers.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{
                        color: colors.emerald,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        fontWeight: '600',
                        marginBottom: '0.75rem'
                      }}>
                        COMPATIBLE ARCHETYPES:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                        {quickCalcResult.compatibleNumbers.map((num: number) => {
                          const arch = getArchetype(num);
                          return (
                            <div
                              key={num}
                              style={{
                                padding: '0.75rem 1rem',
                                background: `
                                  linear-gradient(135deg, 
                                    #2d2d2d 0%, 
                                    #1e1e1e 25%, 
                                    #2a2a2a 50%, 
                                    #1e1e1e 75%, 
                                    #2d2d2d 100%
                                  )
                                `,
                                border: `2px solid ${colors.emerald}`,
                                borderRadius: '4px',
                                boxShadow: `
                                  inset 0 1px 3px rgba(255,255,255,0.1),
                                  inset 0 -1px 3px rgba(0,0,0,0.5),
                                  0 2px 4px rgba(0,0,0,0.6),
                                  0 0 0 1px #404040,
                                  0 0 4px ${colors.emerald}30
                                `,
                                minWidth: '120px'
                              }}
                            >
                              <div style={{
                                color: colors.emerald,
                                fontSize: '1rem',
                                fontWeight: '700',
                                fontFamily: 'monospace',
                                marginBottom: '0.25rem'
                              }}>
                                {num}
                              </div>
                              <div style={{
                                color: colors.light,
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                marginBottom: '0.25rem'
                              }}>
                                {arch.title}
                              </div>
                              <div style={{
                                color: colors.gray,
                                fontSize: '0.65rem',
                                lineHeight: '1.4'
                              }}>
                                {arch.essence}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Letter Breakdown */}
                  <div>
                    <div style={{
                      color: colors.purple,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      marginBottom: '0.75rem'
                    }}>
                      LETTER BREAKDOWN:
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      marginBottom: '0.75rem'
                    }}>
                      {quickCalcResult.letterBreakdown?.map((letter: any, i: number) => (
                        <div
                          key={i}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: `
                              linear-gradient(135deg, 
                                #2d2d2d 0%, 
                                #1e1e1e 25%, 
                                #2a2a2a 50%, 
                                #1e1e1e 75%, 
                                #2d2d2d 100%
                              )
                            `,
                            border: `2px solid ${letter.type === 'vowel' ? colors.rose : colors.blue}`,
                            borderRadius: '4px',
                            boxShadow: `
                              inset 0 1px 3px rgba(255,255,255,0.1),
                              inset 0 -1px 3px rgba(0,0,0,0.5),
                              0 2px 4px rgba(0,0,0,0.6),
                              0 0 0 1px #404040,
                              0 0 4px ${letter.type === 'vowel' ? colors.rose + '30' : colors.blue + '30'}
                            `,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            minWidth: '50px'
                          }}
                        >
                          <div style={{
                            color: colors.light,
                            fontSize: '1rem',
                            fontWeight: '700'
                          }}>
                            {letter.letter}
                          </div>
                          <div style={{
                            color: letter.type === 'vowel' ? colors.rose : colors.blue,
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {letter.value}
                          </div>
                          <div style={{
                            color: colors.gray,
                            fontSize: '0.6rem'
                          }}>
                            {letter.type === 'vowel' ? 'V' : 'C'}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{
                      padding: '0.75rem',
                      background: `
                        linear-gradient(135deg, 
                          #2d2d2d 0%, 
                          #1e1e1e 25%, 
                          #2a2a2a 50%, 
                          #1e1e1e 75%, 
                          #2d2d2d 100%
                        )
                      `,
                      border: `2px solid ${colors.purple}`,
                      borderRadius: '4px',
                      boxShadow: `
                        inset 0 1px 3px rgba(255,255,255,0.1),
                        inset 0 -1px 3px rgba(0,0,0,0.5),
                        0 2px 4px rgba(0,0,0,0.6),
                        0 0 0 1px #404040,
                        0 0 4px ${colors.purple}30
                      `,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: colors.purple,
                      textAlign: 'center'
                    }}>
                      Total: {quickCalcResult.letterBreakdown?.reduce((sum: number, l: any) => sum + l.value, 0) || 0} → {quickCalcResult.expressionNumber}
                      {' • '}
                      Vowels: {quickCalcResult.soulUrgeNumber} • Consonants: {quickCalcResult.personalityNumber}
                    </div>
                  </div>
                </div>

                {/* Additional Archetypal Info */}
                {quickCalcResult.expressionArchetype && (
                  <div style={{
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${colors.indigo}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040,
                      0 0 6px ${colors.indigo}20
                    `,
                    padding: '1.5rem'
                  }}>
                    <div style={{
                      color: colors.indigo,
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      marginBottom: '1rem'
                    }}>
                      ARCHETYPAL ASSOCIATIONS
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '1rem',
                      fontSize: '0.75rem'
                    }}>
                      <div>
                        <div style={{ color: colors.gray, marginBottom: '0.25rem' }}>Color:</div>
                        <div style={{ color: colors.light, fontWeight: '600' }}>
                          {quickCalcResult.expressionArchetype.color}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: colors.gray, marginBottom: '0.25rem' }}>Element:</div>
                        <div style={{ color: colors.light, fontWeight: '600' }}>
                          {quickCalcResult.expressionArchetype.element}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: colors.gray, marginBottom: '0.25rem' }}>Frequency:</div>
                        <div style={{ color: colors.light, fontWeight: '600' }}>
                          {quickCalcResult.expressionArchetype.frequency} Hz
                        </div>
                      </div>
                      {quickCalcResult.expressionArchetype.planet && (
                        <div>
                          <div style={{ color: colors.gray, marginBottom: '0.25rem' }}>Planet:</div>
                          <div style={{ color: colors.light, fontWeight: '600' }}>
                            {quickCalcResult.expressionArchetype.planet}
                          </div>
                        </div>
                      )}
                      {quickCalcResult.expressionArchetype.chakra && (
                        <div>
                          <div style={{ color: colors.gray, marginBottom: '0.25rem' }}>Chakra:</div>
                          <div style={{ color: colors.light, fontWeight: '600' }}>
                            {quickCalcResult.expressionArchetype.chakra}
                          </div>
                        </div>
                      )}
                      <div>
                        <div style={{ color: colors.gray, marginBottom: '0.25rem' }}>Career Paths:</div>
                        <div style={{ color: colors.light, fontSize: '0.7rem', lineHeight: '1.4' }}>
                          {quickCalcResult.expressionArchetype.careerPaths?.slice(0, 3).join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MasterDashboard;
